<template>
	<div class="container_section px-5 pb-5 pt-3">
		<div class="table_header is-flex is-justify-content-flex-end is-align-items-center mb-1">
			<div class="table_actions is-flex">
				<!-- Toggle tabla ↔ comparación. Oculto en modo clientes (los
					gráficos por cliente ya viven arriba de la tabla). El label +
					icono reflejan a qué vista lleva el click: con la tabla visible
					ofrece "Comparación" (icono de línea); con el gráfico visible
					ofrece volver a "Tabla" (icono de grilla). -->
				<button v-if="comparisonEnabled" class="action_btn"
					:class="{ 'is-active': comparisonOpen }"
					@click="toggleComparison">
					<component :is="comparisonOpen ? 'DashboardSquare' : 'ChartLine'"
						class="mr-1" style="width: 16px; height: 16px;" />
					{{ comparisonOpen ? "Tabla" : "Comparación" }}
				</button>
			</div>
		</div>

		<section>
			<!-- Vista de comparación: reemplaza la tabla en la misma card. Los
				estados loading/error/empty de abajo son sólo de la tabla; el
				chart maneja los suyos internamente. -->
			<SalesComparisonChart v-if="comparisonOpen"
				v-bind="comparison"
				@select-item="$emit('comparison-select-item', $event)"
				@add-year="$emit('comparison-add-year', $event)"
				@remove-year="$emit('comparison-remove-year', $event)" />

			<div v-else-if="isLoading" class="loading_state py-6 has-text-centered">
				<p>Cargando datos...</p>
			</div>

			<div v-else-if="error" class="error_state py-6 has-text-centered">
				<p class="has-text-danger">{{ error }}</p>
				<button class="action_btn mt-3" @click="$emit('retry')">Reintentar</button>
			</div>

			<div v-else-if="monthHeaders.length === 0" class="empty_state py-6 has-text-centered">
				<p>No hay datos de ventas para el período seleccionado.</p>
			</div>

			<div v-else class="table_container" ref="mainTable"
				:style="tableMaxHeight ? { maxHeight: tableMaxHeight + 'px' } : null"
				@scroll.passive="onScroll">
				<table class="custom_table">
					<colgroup>
						<col style="width: 200px;" />
						<col style="width: 160px;" v-for="header in monthHeaders" :key="header" />
					</colgroup>
					<thead>
						<tr>
							<th scope="col" class="sticky-column header_label nobreak"></th>
							<th scope="col" class="has-text-right nobreak is-capitalized"
								v-for="header in monthHeaders" :key="header">
								{{ header }}
							</th>
						</tr>
					</thead>
					<tbody>
						<!-- Virtualización de filas: sólo se renderean las filas de la
							ventana visible (+ buffer). Los spacers de arriba/abajo
							ocupan el alto de las filas no renderizadas para que el
							scrollbar y el offset sean los reales. Mantiene el DOM
							~constante aunque haya cientos de miles de clientes. -->
						<tr v-if="topSpacerHeight > 0" class="spacer_row" aria-hidden="true">
							<td class="spacer_cell" :colspan="monthHeaders.length + 1"
								:style="{ height: topSpacerHeight + 'px' }"></td>
						</tr>
						<tr v-for="row in visibleRows" :key="row.label" class="theme_row_white">
							<th scope="row" class="sticky-column nobreak theme_row_white is-capitalized">
								<span class="label_text"
									v-tooltip.right="row.label.length > 30 ? row.label : ''">
									{{ truncateLabel(row.label) }}
								</span>
							</th>
							<td v-for="(val, idx) in row.values" :key="idx"
								class="has-text-right nobreak theme_row_white"
								:class="{ clickable_cell: cellsClickable }"
								v-tooltip.top="cellTooltip(row, idx)"
								@click="onCellClick(monthHeaders[idx], row)">
								{{ parseToMoney(val) }}
							</td>
						</tr>
						<tr v-if="bottomSpacerHeight > 0" class="spacer_row" aria-hidden="true">
							<td class="spacer_cell" :colspan="monthHeaders.length + 1"
								:style="{ height: bottomSpacerHeight + 'px' }"></td>
						</tr>

						<tr class="theme_row_azure">
							<th scope="row" class="sticky-column nobreak theme_row_azure">
								{{ totalRow.label }}
							</th>
							<td v-for="(val, idx) in totalRow.values" :key="'total-' + idx"
								class="has-text-right nobreak theme_row_azure"
								:class="{ clickable_cell: cellsClickable }"
								v-tooltip.top="cellTooltip(totalRow, idx)"
								@click="onCellClick(monthHeaders[idx], null)">
								{{ parseToMoney(val) }}
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</section>
	</div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import { formatVariation } from "@/utils/percentage";
import ChartLine from "@/components/svg/ChartLine.vue";
import DashboardSquare from "@/components/svg/DashboardSquare.vue";
import SalesComparisonChart from "@/components/financialReports/charts/SalesComparisonChart.vue";

// Alto fijo de cada fila (coincide con `td/th { height: 44px }` del scoped CSS).
// Es la base de la virtualización: con filas de alto uniforme el offset de la
// ventana visible es pura aritmética (scrollTop / ROW_HEIGHT), sin medir el DOM.
const ROW_HEIGHT = 44;
// Filas extra rendereadas arriba y abajo de la ventana visible — colchón para
// que un scroll rápido no muestre filas en blanco antes del re-render.
const ROW_OVERSCAN = 6;
// Piso de altura del scroll-container — ≈ thead + 12 filas + totales. La tabla
// nunca baja de acá; desde este piso crece para llenar el espacio que sobre.
const MIN_TABLE_HEIGHT = 44 * 12;
// Margen entre el fondo del scroll-container y el borde del viewport: cubre el
// padding inferior de .container_section (~24px) + el padding-bottom de
// .ventas_tab (40px) para que la tabla llene sin disparar scroll de página.
const TABLE_BOTTOM_GAP = 72;

export default {
	name: "SalesEvolutionTable",

	components: { ChartLine, DashboardSquare, SalesComparisonChart },

	props: {
		monthHeaders: { type: Array, required: true },
		// Lista plana de filas a renderizar (cuentas, subcategorías o clientes
		// según el sub-filtro). Cada row trae `values[]` (monto por bucket) y
		// `prevYearValues[]` (peer del año anterior, null cuando no hay peer)
		// — el tooltip de cada celda usa ambos para el YoY.
		tableRows: { type: Array, required: true },
		totalRow: { type: Object, required: true },
		isLoading: { type: Boolean, default: false },
		error: { type: String, default: "" },
		// Granularidad activa (month/quarter/year/ytd). Hoy sólo decide si las
		// celdas son clickeables (drill-down únicamente en mensual) — el rango
		// de fechas es responsabilidad exclusiva del calendar del TabHeader.
		filterPeriod: { type: String, default: "month" },
		// Habilita el botón "Comparación". El caller lo apaga en modo clientes
		// (los gráficos por cliente ya están arriba de la tabla).
		comparisonEnabled: { type: Boolean, default: false },
		// View-model del gráfico de comparación — se pasa tal cual a
		// SalesComparisonChart vía v-bind (sus keys coinciden 1:1 con las props
		// del chart: series, months, isLoading, error, subItems, selectedItem,
		// years, availableYears). Bundle único para no inflar la firma.
		comparison: { type: Object, default: () => ({}) },
	},

	computed: {
		// Modal de detalle: solo aplica a granularidad mensual. Para quarter/
		// year/ytd una "celda" agregaría varios meses y el modal pierde sentido.
		cellsClickable() {
			return this.filterPeriod === "month";
		},

		// --- Virtualización de filas ---
		// Primera fila de la ventana visible (con overscan), derivada del scroll.
		startIndex() {
			return Math.max(0, Math.floor(this.scrollTop / this.rowHeight) - ROW_OVERSCAN);
		},
		// Última fila (exclusiva). Cantidad visible = alto del viewport / alto de
		// fila + overscan a ambos lados. Antes de la primera medición usamos el
		// piso para renderear suficientes filas y no mostrar una tabla vacía.
		endIndex() {
			const viewport = this.viewportHeight || MIN_TABLE_HEIGHT;
			const count = Math.ceil(viewport / this.rowHeight) + ROW_OVERSCAN * 2;
			return Math.min(this.tableRows.length, this.startIndex + count);
		},
		// Único slice que llega al DOM — su tamaño es ~constante (decenas de
		// filas) sin importar cuántas haya en total.
		visibleRows() {
			return this.tableRows.slice(this.startIndex, this.endIndex);
		},
		// Alto de las filas no rendereadas arriba/abajo: lo absorben dos <tr>
		// spacer para conservar el alto real del scroll y el offset de la ventana.
		// Usan `rowHeight` medido en runtime (no la constante) para que el offset
		// sea exacto pase lo que pase con box-sizing/bordes — si no, un drift de
		// 1px por fila desalinearía la tabla con cientos de miles de filas.
		topSpacerHeight() {
			return this.startIndex * this.rowHeight;
		},
		bottomSpacerHeight() {
			return Math.max(0, this.tableRows.length - this.endIndex) * this.rowHeight;
		},
	},

	data() {
		// tableMaxHeight = null → usa el fallback CSS hasta la primera medición.
		// comparisonOpen → estado de vista local (tabla vs gráfico); UI-only,
		// no se comparte, así que vive en data y no en Vuex.
		// scrollTop/viewportHeight alimentan la virtualización de filas.
		return {
			tableMaxHeight: null,
			comparisonOpen: false,
			scrollTop: 0,
			viewportHeight: 0,
			// Alto real de una fila, medido del DOM tras el primer render (cae al
			// fallback ROW_HEIGHT hasta entonces / en entornos sin layout).
			rowHeight: ROW_HEIGHT,
		};
	},

	mounted() {
		this.$nextTick(() => {
			this.measureAvailableHeight();
			this.measureRowHeight();
			this.updateVisibleRange();
		});
		// La altura disponible depende del viewport y de lo que haya arriba de
		// la tabla (header del tab, sección Resumen colapsable). Recalculamos en
		// resize de ventana y observando el body — su alto cambia cuando Resumen
		// expande/colapsa o llegan datos, desplazando el top de la tabla.
		this._onWindowResize = () => this.measureAvailableHeight();
		window.addEventListener("resize", this._onWindowResize);
		if (typeof ResizeObserver !== "undefined") {
			this._layoutObserver = new ResizeObserver(() => this.measureAvailableHeight());
			this._layoutObserver.observe(document.body);
		}
	},

	beforeDestroy() {
		if (this._layoutObserver) {
			this._layoutObserver.disconnect();
			this._layoutObserver = null;
		}
		if (this._onWindowResize) {
			window.removeEventListener("resize", this._onWindowResize);
			this._onWindowResize = null;
		}
		if (this._scrollRaf && typeof window !== "undefined" && window.cancelAnimationFrame) {
			window.cancelAnimationFrame(this._scrollRaf);
			this._scrollRaf = null;
		}
	},

	methods: {
		parseToMoney,
		// Alterna tabla ↔ comparación. Emite `toggle-comparison` con el nuevo
		// estado para que el caller dispare el fetch lazy del gráfico la
		// primera vez que se abre (y refetchee mientras esté abierto).
		toggleComparison() {
			this.comparisonOpen = !this.comparisonOpen;
			this.$emit("toggle-comparison", this.comparisonOpen);
		},
		truncateLabel(label) {
			return label.length > 30 ? label.substring(0, 30) + "..." : label;
		},
		scrollToRight() {
			this.$nextTick(() => {
				const table = this.$refs.mainTable;
				if (table) table.scrollLeft = table.scrollWidth;
			});
		},
		// Cap de altura del scroll-container = espacio entre su top y el fondo
		// del viewport (menos el gap inferior), con piso MIN_TABLE_HEIGHT. Es
		// un `max-height`, no `height`: con pocas filas la tabla se ajusta a su
		// contenido (sin vacío), y solo cuando el contenido excede este cap
		// llena el espacio y scrollea internamente (`overflow: auto`).
		measureAvailableHeight() {
			const el = this.$refs.mainTable;
			if (!el) return;
			const top = el.getBoundingClientRect().top;
			const available = window.innerHeight - top - TABLE_BOTTOM_GAP;
			this.tableMaxHeight = Math.max(MIN_TABLE_HEIGHT, Math.round(available));
			// El alto del viewport cambió → recomputa la ventana visible.
			this.viewportHeight = el.clientHeight;
		},

		// Lee scroll + alto del container y los vuelca a data; los computed de la
		// ventana virtual reaccionan. Separado del handler de scroll para poder
		// invocarlo directo (mount, resize, cambio de datos) sin un evento.
		updateVisibleRange() {
			const el = this.$refs.mainTable;
			if (!el) return;
			this.scrollTop = el.scrollTop;
			this.viewportHeight = el.clientHeight;
		},

		// Mide el alto real de una fila renderada para que la aritmética de la
		// virtualización no dependa de un valor hardcodeado. No-op si todavía no
		// hay filas o el entorno no calcula layout (offsetHeight 0).
		measureRowHeight() {
			const el = this.$refs.mainTable;
			if (!el) return;
			const row = el.querySelector("tbody tr.theme_row_white");
			if (row && row.offsetHeight > 0) this.rowHeight = row.offsetHeight;
		},

		// Scroll vertical → recomputa la ventana, throttleado a un frame para no
		// recalcular en cada tick del scroll. rAF cuando existe; setTimeout como
		// fallback (entornos sin rAF, p.ej. algunos test runners).
		onScroll() {
			if (this._scrollRaf) return;
			const schedule = (typeof window !== "undefined" && window.requestAnimationFrame)
				? window.requestAnimationFrame.bind(window)
				: (cb) => setTimeout(cb, 16);
			this._scrollRaf = schedule(() => {
				this._scrollRaf = null;
				this.updateVisibleRange();
			});
		},
		// YoY de una celda contra su peer del año anterior. Devuelve {text, color}
		// para que el caller decida cómo serializarlo. `prevYear` null → "—" y
		// color neutro (no se inventa peer); `prevYear` 0 con valor actual
		// distinto a 0 → también "—" porque la división explota.
		yoyFor(value, prevYear) {
			if (prevYear == null || prevYear === 0) {
				return { text: "—", color: "var(--report-text-muted)" };
			}
			const ratio = value / prevYear - 1;
			let color = "var(--report-text-muted)";
			if (ratio > 0) color = "var(--report-positive)";
			else if (ratio < 0) color = "var(--report-negative)";
			return {
				text: formatVariation(ratio, { withArrow: true, withSign: true }),
				color,
			};
		},
		// `% de ventas` de una celda = celda / Ventas Netas en la MISMA columna.
		// Para la fila total el ratio es siempre 1 (el total es el total), así
		// que omitimos esa línea — el tooltip de la fila total muestra sólo YoY.
		salesShareFor(value, idx) {
			const total = this.totalRow?.values?.[idx];
			if (!total) return "—";
			return formatVariation(value / total);
		},
		// Tooltip per-cell. Reemplaza al tooltip de la columna Acumulado que
		// vivía en la 2da sticky; ahora cada celda mensual lo carga. HTML inline
		// porque floating-vue resuelve `var(--…)` dentro del portal (corre fuera
		// del subárbol scoped pero hereda :root). Para la fila total omitimos
		// el "% de ventas" (sería siempre 100%, redundante).
		cellTooltip(row, idx) {
			const value = row.values[idx] ?? 0;
			const prevYear = row.prevYearValues ? row.prevYearValues[idx] : null;
			const yoy = this.yoyFor(value, prevYear);
			let content = "";
			if (!row.isTotal) {
				content += `% de ventas: <span style="font-weight:600;">${this.salesShareFor(value, idx)}</span><br>`;
			}
			content += `YoY: <span style="color:${yoy.color};font-weight:600;">${yoy.text}</span>`;
			return { content, html: true };
		},
		// El payload del evento `cell-click` lleva el label de la fila como
		// `account` (cuenta, subcategoría o cliente según el sub-filtro) para
		// que el padre decida cómo abrir el drill-down. La fila total emite
		// `account: null`.
		onCellClick(monthLabel, row) {
			if (!this.cellsClickable) return;
			this.$emit("cell-click", {
				monthLabel,
				account: row ? row.label : null,
			});
		},
	},

	watch: {
		// Lista nueva (cambio de filtro o de datos): volvemos al tope y
		// recomputamos la ventana, para no quedar scrolleados más allá del nuevo
		// total ni mostrar una ventana stale.
		tableRows() {
			const el = this.$refs.mainTable;
			if (el) el.scrollTop = 0;
			this.scrollTop = 0;
			this.$nextTick(() => {
				this.measureRowHeight();
				this.updateVisibleRange();
			});
		},
		monthHeaders() {
			this.scrollToRight();
			this.$nextTick(() => {
				this.measureAvailableHeight();
			});
		},
		isLoading(val) {
			if (!val) {
				this.scrollToRight();
				this.$nextTick(() => {
					this.measureAvailableHeight();
				});
			}
		},
		// Si el caller deshabilita la comparación (cambio a modo clientes) con
		// el panel abierto, lo cerramos para no dejar un gráfico colgado sin su
		// botón de toggle.
		comparisonEnabled(enabled) {
			if (!enabled && this.comparisonOpen) {
				this.comparisonOpen = false;
				this.$emit("toggle-comparison", false);
			}
		},
	},
};
</script>

<style scoped lang="scss">
.container_section {
	background: var(--report-bg);
	border-radius: 16px;
	border: 1px solid var(--report-border);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}


.action_btn {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 6px 12px;
	font-size: 14px;
	font-weight: 500;
	color: var(--report-text-muted-strong);
	display: flex;
	align-items: center;
	cursor: pointer;

	&:hover {
		background: var(--report-surface);
	}

	// Estado activo: el toggle "Comparación" queda resaltado mientras el
	// gráfico está abierto, para señalar que la card está en modo comparación.
	&.is-active {
		background: var(--report-accent-bg);
		border-color: var(--report-accent-border);
		color: var(--report-accent-text);
	}
}

.table_container {
	// Scroll en ambos ejes: x para el ancho del periodo, y para listas largas.
	overflow: auto;
	border: 1px solid var(--report-border);
	border-radius: 12px;
	background: var(--report-bg);
	max-width: 100%;
	// Cap pre-medición (≈ thead + 12 filas + totales). En runtime,
	// `measureAvailableHeight()` lo sobre-escribe con un `max-height` inline =
	// espacio disponible hasta el fondo del viewport (piso = este valor). Es
	// max-height (no height): con pocas filas la tabla se ajusta a su contenido
	// sin dejar vacío; con muchas, llena el espacio y scrollea acá.
	max-height: calc(44px * 12);
	// Aísla este subárbol del resto del documento — repaints/reflows no se propagan.
	contain: layout paint;
}

// Única columna sticky (label de la fila). Ancho flexible: el `<col width:
// 200px>` queda como hint inicial en auto-layout — puede crecer si el
// contenido es más largo.
.sticky-column {
	position: sticky;
	left: 0;
	z-index: 2;
	background-color: inherit;
	border-right: 1px solid var(--report-border);
}

.custom_table {
	min-width: 100%;
	border-collapse: separate;
	border-spacing: 0;

	tbody tr {
		// Cada fila aislada: cambios en una fila no fuerzan layout del resto.
		contain: layout style;
	}

	td,
	th {
		height: 44px;
		padding: 12px 16px !important;
		border-bottom: 1px solid var(--report-divider-soft);
		vertical-align: middle;
	}

	thead th,
	td {
		text-align: end;
	}

	thead th {
		padding: 12px 24px;
		color: var(--report-text-muted);
		font-size: 12px;
		font-weight: 700;
		line-height: 18px;
		background-color: var(--report-surface);
		// Sticky-top: el encabezado queda visible al scrollear verticalmente.
		// El background-color es indispensable: como sticky pinta fuera del
		// flujo normal, sin él se vería el contenido del body atrás.
		position: sticky;
		top: 0;
		z-index: 3;
	}

	tbody th {
		color: var(--report-text);
		font-size: 12px;
		font-weight: 500;
		line-height: 18px;
	}

	tbody td {
		color: var(--report-text-muted-strong);
		font-size: 14px;
		font-weight: 400;
		line-height: 20px;
	}

	.theme_row_white {
		background-color: var(--report-bg);
	}

	// Affordance de drill-down: solo aplica a celdas mensuales de filas de
	// datos (no totales). El selector :hover sobre <td> mantiene el cambio
	// localizado a la celda apuntada — no toda la fila.
	.clickable_cell {
		cursor: pointer;
		transition: background-color 0.12s ease;

		&:hover {
			background-color: var(--report-accent-bg);
		}
	}

	.header_label {
		background-color: var(--report-surface);
	}

	.theme_row_azure {
		background-color: var(--report-row-azure);
		font-weight: 600;

		// Sticky-bottom: la fila de totales (Ventas Netas) queda anclada al
		// borde inferior del scroll-container. Aplicado a las celdas (no al
		// <tr>) porque algunos engines no soportan sticky en filas de tabla.
		th, td {
			position: sticky;
			bottom: 0;
			z-index: 3;
		}
	}

	// Esquinas: las celdas que son sticky en AMBOS ejes (top + left) deben
	// pintar por encima de los stickies de un solo eje, por eso suben a z-index
	// 4. Hoy aplica al thead-left y al total-row-left.
	thead .sticky-column,
	.theme_row_azure .sticky-column {
		z-index: 4;
	}
}

// Filas espaciadoras de la virtualización: sólo aportan alto (el de las filas
// fuera de la ventana). Sin padding, borde ni contenido — su `height` inline
// manda. `pointer-events:none` para que no intercepten clicks.
.spacer_row .spacer_cell {
	padding: 0 !important;
	border: 0 !important;
	pointer-events: none;
}

.loading_state,
.empty_state,
.error_state {
	color: var(--report-text-muted);
	font-size: 14px;
}

.nobreak {
	white-space: nowrap;
}
</style>
